//#region Imports

// Angular imports
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    OnDestroy,
    Output,
    ViewChild,
    OnChanges,
    SimpleChanges
} from '@angular/core';

// Dev extreme imports
import { RowClickEvent, RowPreparedEvent, SelectionChangedEvent } from 'devextreme/ui/tree_list';

// Rxjs
import { Subscription } from 'rxjs';

// cc-common imports
import { DxWidgetUtils, Utils } from 'cc-common';

// component imports
import { DxDropDownBoxComponent, DxTextBoxComponent, DxTreeListComponent } from 'devextreme-angular';

// Constant imports
import { ContextTypeConstants } from '../_constants/context-type-constants';

// service imports
import { UserContextTreeService } from '../../_services/data-services/user-context-tree.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { ConfigurationService } from '../../_services/shared-services/configuration.service';

// model imports
import { UserContextTreeResultDto } from '../../_models/user-context-tree-result-dto';
import { UserContextTreeParametersDto } from '../../_models/user-context-tree-parameters-dto';

//#endregion

@Component({
    selector: 'app-user-context-tree',
    templateUrl: './user-context-tree.component.html',
    styleUrls: ['./user-context-tree.component.scss']
})
export class UserContextTreeComponent implements OnInit, OnChanges, OnDestroy {
    //#region Component Properties

    @ViewChild(DxTreeListComponent, { static: false }) treeView: DxTreeListComponent;
    @ViewChild('dropDownBox', { static: false }) dropdownBox: DxDropDownBoxComponent;
    @ViewChild('dropDownBoxTextBox', { static: false }) dropDownBoxTextBox: DxTextBoxComponent;
    @Input() allowGlobalSelection = false;
    @Input() allowManagementCompanySelection = true;
    @Input() allowLocationSelection = true;
    @Input() allowCommunitySelection = true;
    @Input() showGlobalNode = false;
    @Input() includeTerminated = false;
    @Input() selectionMode: 'single' | 'multiple' = 'single';
    @Input() contextTypeSelectionMode: 'single' | 'multiple' = 'multiple';
    @Input() selectedContext: any;
    @Input() customizeLabel: (selected: any) => string;
    @Input() contextTreeBoxValue: string[] = [];
    @Input() showClearButton = true;
    @Input() defaultContext: any;
    @Input() placeholder = 'Select a value...';
    @Input() cssClass = 'drop-down-box';
    @Input() contextTreeDataSource: UserContextTreeResultDto[] = [];
    @Input() readOnly = false;
    @Input() disabled = false;
    @Input() hasParentDataSource = false;
    @Output() contextTreeBoxValueChange = new EventEmitter<string[]>();
    @Output() getContextTreeSelectedData = new EventEmitter<any>();
    @Output() contextTreeDataSourceChange = new EventEmitter<UserContextTreeResultDto[]>();
    @Output() onContextSelectionClosed = new EventEmitter<string[]>();

    elementAttr: object = {};
    focusedRowKey: string;
    textInputVal: string;
    showUserContextTree = true;
    autoExpandAll = false;
    rootValue: number;
    refreshSubscription: Subscription; // to be notified when includeTerminated flag is changed or refresh button from the configuration-selector is clicked.

    //#endregion

    private _timerId: any;

    //#region Component life cycle methods

    constructor(
        private readonly contextTreeService: UserContextTreeService,
        private readonly configService: ConfigurationService,
        private readonly errorService: ServerErrorService
    ) {
        this.expandAllNodes = this.expandAllNodes.bind(this);
        this.collapseAllNodes = this.collapseAllNodes.bind(this);
    }

    ngOnInit(): void {
        this.elementAttr = { class: this.cssClass };

        this.refreshSubscription = this.configService
            .getSubjectForceRefresh()
            .subscribe((includeTerminated: boolean) => {
                this.includeTerminated = includeTerminated;
                this.populateContextTreeData();
            });

        if (Utils.notNullAndDefined(this.contextTreeDataSource) && this.contextTreeDataSource.length > 0) {
            this.contextTreeDataSource = this.processContextTreeData(this.contextTreeDataSource);
        } else if (Utils.isFalse(this.hasParentDataSource)) {
            this.populateContextTreeData();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (Utils.notNullAndDefined(changes.contextTreeDataSource)) {
            // If Configuration (context tree data source) was changed then reset focused row
            this.focusedRowKey = null;
        }

        if (Utils.notNullOrEmpty(this.contextTreeDataSource)) {
            this.contextTreeDataSource = this.processContextTreeData(this.contextTreeDataSource);
        }
    }

    ngOnDestroy(): void {
        if (Utils.notNullAndDefined(this.refreshSubscription)) {
            this.refreshSubscription.unsubscribe();
        }
    }

    //#endregion

    //#region Event Handlers

    onTextInput(): void {
        this.dropdownBox.instance.open();
        setTimeout(() => {
            this.dropDownBoxTextBox.instance.focus();
        }, 100);
    }

    onTextValueChange(event): void {
        if (event.value) {
            this.textInputVal = event.value;
        } else if (this.textInputVal) {
            this.textInputVal = null;
            this.contextTreeBoxValue = [];
            if (this.treeView && this.treeView.instance) {
                this.treeView.instance.searchByText(null);
            }
            this.getContextTreeSelectedData.emit(Utils.equalsIgnoreCase(this.selectionMode, 'single') ? null : []);
        }
    }

    onTextEditorEnterKey(): void {
        this.dropdownBox.instance.close();
        setTimeout(() => {
            this.dropDownBoxTextBox.instance.focus();
        }, 100);
    }

    onDropdownKeyUp(event): void {
        if (Utils.isFalse(this.readOnly)) {
            const textToSearch = event.event.target.value;
            if (textToSearch) {
                this.treeView.instance.searchByText(textToSearch);
            } else {
                this.textInputVal = null;
                if (this.treeView && this.treeView.instance) {
                    this.treeView.instance.searchByText(null);
                }
            }
        }
    }

    onDropdownOpen(): void {
        // Collapse all nodes
        this.collapseAllNodes();

        setTimeout(() => {
            this.dropDownBoxTextBox.instance.focus();

            if (Utils.notNullOrEmpty(this.contextTreeBoxValue)) {
                // This will select and focus the row
                this.focusedRowKey = this.contextTreeBoxValue[0];
            } else {
                // When no node is selected, open the Global node item by default if present
                if (Utils.isTrue(this.showGlobalNode)) {
                    // Find global node
                    const globalNode = this.contextTreeDataSource.find((item) =>
                        Utils.equalsIgnoreCase(item.contextType, ContextTypeConstants.GLOBAL)
                    );

                    if (DxWidgetUtils.hasInstance(this.treeView) && Utils.notNullAndDefined(globalNode)) {
                        this.treeView.instance.expandRow(globalNode.context);
                    }
                }
            }
        }, 150);
    }

    onDropdownClosed(): void {
        this.onContextSelectionClosed.emit();
    }

    onContextTreeRowPrepared(e: RowPreparedEvent): void {
        if (Utils.equalsIgnoreCase(e.rowType, 'data')) {
            if (Utils.isTrue(e.data.disabledSelection) || Utils.equalsIgnoreCase(e.data.context, this.defaultContext)) {
                e.rowElement.classList.add('hide-checkbox-selection');
            } else if (
                Utils.equalsIgnoreCase(e.data.contextType, ContextTypeConstants.COMMUNITY) &&
                Utils.isTrue(e.data.isTerminated)
            ) {
                e.rowElement.classList.add('cc-red-icon');
            }
        }
    }

    onContextTreeSelectionChanged(e: SelectionChangedEvent): void {
        clearTimeout(this._timerId);

        this._timerId = setTimeout(() => {
            const seletableRows = this.getSelectableRows(e);

            if (seletableRows.length > 0) {
                if (Utils.equalsIgnoreCase(this.selectionMode, 'single')) {
                    this.textInputVal = Utils.notNullAndDefined(this.customizeLabel)
                        ? this.customizeLabel(seletableRows[0])
                        : seletableRows[0].title;

                    this.getContextTreeSelectedData.emit(seletableRows[0]);
                    this.dropdownBox.instance.close();
                } else {
                    this.textInputVal = Utils.notNullAndDefined(this.customizeLabel)
                        ? this.customizeLabel(seletableRows)
                        : `${seletableRows.length} Selected`;
                    this.getContextTreeSelectedData.emit(seletableRows);
                }
            } else {
                this.textInputVal = null;
                this.getContextTreeSelectedData.emit(Utils.equalsIgnoreCase(this.selectionMode, 'single') ? null : []);
            }
            const selectableKeys = seletableRows.map((r) => r.context);
            this.contextTreeBoxValue = selectableKeys;
            this.contextTreeBoxValueChange.emit(selectableKeys);
        }, 250);
    }

    onContextTreeRowClick(e: RowClickEvent): void {
        if (DxWidgetUtils.hasInstance(this.treeView)) {
            // Provide the feature of collapse/expand on disabled row click
            const data: UserContextTreeResultDto = e.data;
            if (
                Utils.notNullAndDefined(data) &&
                Utils.isTrue(data.disabledSelection) &&
                Utils.equalsIgnoreCase(e.event.target.tagName, 'DIV') // If row div click was clicked instead of collapse/expand left arrow icon
            ) {
                e.isExpanded ? this.treeView.instance.collapseRow(e.key) : this.treeView.instance.expandRow(e.key);
            }
        }
    }

    //#endregion

    collapseAllNodes(): void {
        // Reset the focused row
        this.focusedRowKey = null;

        this.showUserContextTree = false;
        this.autoExpandAll = false;
        setTimeout(() => (this.showUserContextTree = true), 50);
    }

    expandAllNodes(): void {
        this.showUserContextTree = false;
        this.autoExpandAll = true;

        setTimeout(() => {
            // Set the focused row
            this.focusedRowKey = this.selectedContext;
            this.showUserContextTree = true;
        }, 50);
    }

    public repaint(): void {
        if (Utils.notNullAndDefined(this.dropdownBox) && Utils.notNullAndDefined(this.dropdownBox.instance)) {
            this.dropdownBox.instance.open();

            setTimeout(() => {
                this.dropdownBox.instance.close();
            }, 200);
        }
    }

    //#region Private Methods

    private getSelectableRows(e: any): UserContextTreeResultDto[] {
        let selectableRows = e.selectedRowsData as UserContextTreeResultDto[];

        // Check if selectedRows consist of any row with disabled selection. If yes then remove them from the list
        selectableRows = selectableRows.filter((row) => Utils.isFalse(row.disabledSelection));

        /**
         * Logic below is to control selection of only a single type of context but not single node.
         * When contextTypeSelectionMode is single, we must allow user to select either
         * "Only Communities / Sub Communities under one Location" or
         * "Only Locations under same Management Company" or "Only Management Companies".
         * Hence we filter currently selectedRows based on user's current selection by clicking on a node.
         */

        const selectedKey = e.currentSelectedRowKeys[0];
        if (Utils.equalsIgnoreCase(this.contextTypeSelectionMode, 'single') && Utils.notNullAndDefined(selectedKey)) {
            const selectedRow = this.contextTreeDataSource.find((r) => Utils.equalsIgnoreCase(r.context, selectedKey));
            const parentRow = this.contextTreeDataSource.find((r) =>
                Utils.equalsIgnoreCase(r.context, selectedRow.parentContext)
            );

            selectableRows = selectableRows.filter((r) => {
                let isValid =
                    Utils.equalsIgnoreCase(r.contextType, selectedRow.contextType) &&
                    Utils.equalsIgnoreCase(r.parentContext, selectedRow.parentContext);
                // For Master and Sub Community, we should validate based on parent context types
                if (
                    Utils.isFalse(isValid) &&
                    Utils.notNullAndDefined(parentRow) &&
                    Utils.equalsIgnoreCase(parentRow.contextType, ContextTypeConstants.COMMUNITY)
                ) {
                    isValid =
                        Utils.equalsIgnoreCase(r.contextType, parentRow.contextType) &&
                        Utils.equalsIgnoreCase(r.parentContext, parentRow.parentContext);
                }
                if (!isValid) {
                    const pr = this.contextTreeDataSource.find((p) =>
                        Utils.equalsIgnoreCase(p.context, r.parentContext)
                    );
                    if (
                        Utils.notNullAndDefined(pr) &&
                        Utils.equalsIgnoreCase(pr.contextType, ContextTypeConstants.COMMUNITY)
                    ) {
                        isValid = Utils.equalsIgnoreCase(pr.contextType, selectedRow.contextType);
                    }
                }
                return isValid;
            });
        }

        return selectableRows;
    }

    private populateContextTreeData(): void {
        const param = new UserContextTreeParametersDto();
        param.includeGlobalNode = this.showGlobalNode;
        param.includeTerminated = this.includeTerminated;
        this.contextTreeService.getUserContextTree(param).subscribe(
            (results) => {
                results = this.processContextTreeData(results);
                this.contextTreeDataSource = results;
                this.contextTreeDataSourceChange.emit(results);
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private processContextTreeData(results: UserContextTreeResultDto[]): UserContextTreeResultDto[] {
        this.rootValue = this.showGlobalNode ? null : 0;
        if (
            !results.find((r) => Utils.equalsIgnoreCase(r.contextType, ContextTypeConstants.GLOBAL)) &&
            this.showGlobalNode
        ) {
            const gn = new UserContextTreeResultDto();

            gn.title = ContextTypeConstants.GLOBAL;
            gn.contextType = ContextTypeConstants.GLOBAL;
            gn.context = 'G0';
            gn.parentContext = null;

            results.push(gn);
        } else if (
            !results.find((r) => Utils.equalsIgnoreCase(r.contextType, ContextTypeConstants.GLOBAL)) &&
            Utils.isFalse(this.showGlobalNode)
        ) {
            results.forEach((r) => {
                if (Utils.equalsIgnoreCase(r.parentContext, 'G0')) {
                    r.parentContext = null;
                }
            });
        }

        if (Utils.isFalse(this.allowCommunitySelection)) {
            results = results.filter((r) => r.contextType !== 'Community');
        }
        const selectedItems = [];
        results.forEach((e) => {
            if (!e.disabledSelection) {
                if (
                    (Utils.equalsIgnoreCase(e.contextType, ContextTypeConstants.GLOBAL) &&
                        Utils.isFalse(this.allowGlobalSelection)) ||
                    (Utils.equalsIgnoreCase(e.contextType, ContextTypeConstants.MANAGEMENTCOMPANY) &&
                        Utils.isFalse(this.allowManagementCompanySelection)) ||
                    (Utils.equalsIgnoreCase(e.contextType, ContextTypeConstants.LOCATION) &&
                        Utils.isFalse(this.allowLocationSelection)) ||
                    (Utils.equalsIgnoreCase(e.contextType, ContextTypeConstants.COMMUNITY) &&
                        Utils.isFalse(this.allowCommunitySelection))
                ) {
                    e.disabledSelection = true;
                }
            }
            if (
                (Utils.notNullAndDefined(this.selectedContext) &&
                    Utils.equalsIgnoreCase(e.context, this.selectedContext)) ||
                (Utils.notNullAndDefined(this.defaultContext) && Utils.equalsIgnoreCase(e.context, this.defaultContext))
            ) {
                this.contextTreeBoxValue.push(e.context);
                selectedItems.push(e);
            } else if (this.contextTreeBoxValue.length > 0 && this.contextTreeBoxValue.indexOf(e.context) !== -1) {
                selectedItems.push(e);
            }
        });
        if (selectedItems.length > 0) {
            if (Utils.equalsIgnoreCase(this.selectionMode, 'single')) {
                this.textInputVal = Utils.notNullAndDefined(this.customizeLabel)
                    ? this.customizeLabel(selectedItems[0])
                    : selectedItems[0].title;
            } else {
                this.textInputVal = Utils.notNullAndDefined(this.customizeLabel)
                    ? this.customizeLabel(selectedItems)
                    : `${selectedItems.length} Selected`;
            }
        } else {
            this.textInputVal = null;
        }
        return results;
    }

    //#endregion
}
